<template>
  <div class="tw-relative tw-w-full">
    <input
      v-bind="$attrs"
      ref="searchRef"
      v-model="proxy"
      type="search"
      class="tw-block tw-w-full tw-rounded tw-border tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all"
      :class="classes"
      :data-test-id="testId"
      @blur="onBlur"
    />
    <span
      class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-dorian-gray-200"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="icon icon-tabler icon-tabler-search"
        :width="iconSize"
        :height="iconSize"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
        <path d="M21 21l-6 -6" />
      </svg>
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRefs } from "vue";

import type { VNodeRef } from "vue";
import type { Size } from "./types";

export interface InputSearchProps {
  value: string;
  className?: string;
  size?: Size;
  state?: boolean;
  testId?: string;
}

const props = withDefaults(defineProps<InputSearchProps>(), {
  size: "medium",
  className: undefined,
  state: undefined,
  ref: undefined,
  testId: "input-search",
});

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-3 tw-pl-9",
  medium: "tw-text-base tw-py-2 tw-px-3 tw-pl-10",
  large: "tw-text-lg tw-py-3 tw-px-3 tw-pl-12",
};

const states = {
  none: "tw-border-dorian-gray-100/80",
  valid: "tw-border-success-500",
  error: "tw-border-danger-500",
};

const focusClasses =
  "focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10";

const disabledClasses =
  "disabled:tw-cursor-not-allowed disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none";

const readonlyClasses =
  "read-only:tw-cursor-default read-only:tw-bg-dorian-gray-50 read-only:tw-text-dorian-gray-500/50 read-only:tw-outline-none";

const searchRef = ref<VNodeRef | null>(null);
const focus = () => {
  if (searchRef.value) {
    searchRef.value.focus();
  }
};

const { className, size, state, value } = toRefs(props);

const classes = computed(() => {
  return [
    sizes[size.value],
    state.value === undefined
      ? states.none
      : props.state
        ? states.valid
        : states.error,
    focusClasses,
    disabledClasses,
    readonlyClasses,
    className.value,
  ];
});

const iconSize = computed(() => {
  if (size.value === "small") {
    return "14";
  }

  if (size.value === "medium") {
    return "18";
  }

  return "24";
});
const proxy = computed({
  get() {
    return value.value;
  },
  set(value: string) {
    emit("update:value", value);
  },
});

const onBlur = () => {
  if (typeof value.value === "string") {
    const trimmed = value.value.trim();
    emit("update:value", trimmed);
  }
};

const emit = defineEmits<{
  "update:value": [value: string];
}>();

defineExpose({
  focus,
});
</script>
